import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Autocomplete,
  Alert,
  CircularProgress,
  Fade,
  IconButton,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Code as CodeIcon,
  Description as DescriptionIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  createService,
  getCategories
} from '../lib/api/services';
import { getCurrentUserProfile } from '../lib/api/profile';
import { trackServiceCreation } from '../lib/analytics';

interface Category {
  id: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  sort_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface ServiceFormData {
  title: string;
  description: string;
  price: number | null;
  price_type: 'FIXED' | 'HOURLY' | 'DAILY' | 'WEEKLY' | 'MONTHLY';
  category_id: string;
  skills: string[];
  delivery_days: number | null;
  revisions: number;
  features: Array<{
    id: string;
    name: string;
    included: boolean;
  }>;
}

const NewServicePage: React.FC = () => {
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  
  // Form data
  const [formData, setFormData] = useState<ServiceFormData>({
    title: '',
    description: '',
    price: null,
    price_type: 'FIXED',
    category_id: '',
    skills: [],
    delivery_days: null,
    revisions: 1,
    features: []
  });


  // Skills options
  const skillOptions = [
    'React', 'Vue.js', 'Angular', 'JavaScript', 'TypeScript', 'Node.js', 'Python', 'Java', 'C#', 'PHP',
    'HTML', 'CSS', 'SASS', 'SCSS', 'Bootstrap', 'Material-UI', 'Tailwind CSS', 'jQuery',
    'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'Firebase', 'Supabase',
    'AWS', 'Azure', 'Google Cloud', 'Docker', 'Kubernetes', 'Git', 'GitHub', 'GitLab',
    'Figma', 'Adobe XD', 'Sketch', 'Photoshop', 'Illustrator',
    'WordPress', 'Shopify', 'WooCommerce', 'Magento', 'Drupal',
    'Mobile Development', 'iOS', 'Android', 'React Native', 'Flutter', 'Xamarin',
    'Machine Learning', 'AI', 'Data Science', 'Blockchain', 'Web3', 'NFT',
    'E-commerce', 'Payment Integration', 'API Development', 'REST API', 'GraphQL',
    'DevOps', 'CI/CD', 'Testing', 'Jest', 'Cypress', 'Selenium'
  ];

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Check user role
      const userProfile = await getCurrentUserProfile();
      if (!userProfile) {
        setError('Трябва да влезете в профила си, за да публикувате услуга');
        return;
      }
      
      if (userProfile.role !== 'FREELANCER' && userProfile.role !== 'AGENCY') {
        setError('Само фриленсъри и агенции могат да публикуват услуги');
        return;
      }
      
      setUserRole(userProfile.role);
      
      // Load categories
      console.log('Loading categories...');
      const categoriesData = await getCategories();
      console.log('Categories loaded:', categoriesData);
      setCategories(categoriesData);
      
    } catch (err) {
      console.error('Error loading initial data:', err);
      setError('Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ServiceFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSkillsChange = (newSkills: string[]) => {
    setFormData(prev => ({
      ...prev,
      skills: newSkills
    }));
  };

  const handleAddFeature = () => {
    const newFeature = {
      id: Date.now().toString(),
      name: '',
      included: true
    };
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, newFeature]
    }));
  };

  const handleUpdateFeature = (featureId: string, field: 'name' | 'included', value: any) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.map(feature =>
        feature.id === featureId ? { ...feature, [field]: value } : feature
      )
    }));
  };

  const handleRemoveFeature = (featureId: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter(feature => feature.id !== featureId)
    }));
  };

  const validateForm = (): string | null => {
    if (!formData.title.trim()) {
      return 'Заглавието е задължително';
    }
    
    if (formData.title.length < 10) {
      return 'Заглавието трябва да бъде поне 10 символа';
    }
    
    if (!formData.description.trim()) {
      return 'Описанието е задължително';
    }
    
    if (formData.description.length < 50) {
      return 'Описанието трябва да бъде поне 50 символа';
    }
    
    if (!formData.price || formData.price <= 0) {
      return 'Цената трябва да бъде по-голяма от 0';
    }
    
    if (!formData.delivery_days || formData.delivery_days <= 0) {
      return 'Срокът за доставка трябва да бъде по-голям от 0 дни';
    }
    
    if (formData.skills.length === 0) {
      return 'Изберете поне едно умение';
    }
    
    if (formData.features.some(f => !f.name.trim())) {
      return 'Всички функции трябва да имат име';
    }
    
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const serviceData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: formData.price!,
        price_type: formData.price_type,
        category_id: formData.category_id || undefined,
        skills: formData.skills,
        delivery_days: formData.delivery_days!,
        revisions: formData.revisions,
        features: formData.features.filter(f => f.name.trim())
      };
      
      console.log('Submitting service data:', serviceData);
      const result = await createService(serviceData);
      console.log('Service created successfully:', result);
      
      // Track service creation
      const priceRange = formData.price 
        ? `${formData.price}`
        : 'not_specified';
      trackServiceCreation(formData.category_id || 'general', priceRange);
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/find-services');
      }, 2000);
      
    } catch (err: any) {
      console.error('Error creating service:', err);
      const errorMessage = err?.message || err?.error?.message || 'Грешка при създаване на услугата';
      setError(`Грешка: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData({
      title: '',
      description: '',
      price: null,
      price_type: 'FIXED',
      category_id: '',
      skills: [],
      delivery_days: null,
      revisions: 1,
      features: []
    });
    setError(null);
  };

  if (loading) {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#28A0F0' }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  if (userRole !== 'FREELANCER' && userRole !== 'AGENCY') {
    return (
      <Box sx={{ 
        backgroundColor: '#0f0f23', 
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Header />
        <Container maxWidth="md" sx={{ py: 8, position: 'relative', zIndex: 1 }}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 4,
              p: 4,
              textAlign: 'center'
            }}
          >
            <Typography variant="h4" sx={{ color: '#ef4444', mb: 2 }}>
              Достъп отказан
            </Typography>
            <Typography variant="body1" sx={{ color: '#ffffff', mb: 3 }}>
              Само фриленсъри и агенции могат да публикуват услуги. Моля, влезте с подходящ профил.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                color: '#ffffff',
              }}
            >
              Начална страница
            </Button>
          </Card>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      backgroundColor: '#0f0f23', 
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated background shapes */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
          borderRadius: '50%',
          opacity: 0.3,
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
            '50%': { transform: 'translateY(-20px) rotate(180deg)' },
          },
        }}
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          right: '15%',
          width: 40,
          height: 40,
          background: 'linear-gradient(135deg, #8b5cf6 0%, #28A0F0 100%)',
          borderRadius: '20%',
          opacity: 0.4,
          animation: 'float 8s ease-in-out infinite reverse',
        }}
      />

      <Header />
      
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton
              onClick={() => navigate('/find-services')}
              sx={{ color: '#28A0F0', mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <BusinessIcon sx={{ color: '#28A0F0', mr: 1, fontSize: '2rem' }} />
            <Typography 
              variant="h3" 
              sx={{ 
                color: '#ffffff', 
                fontWeight: 800,
                background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Публикувай услуга
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ color: '#a0a0a0', ml: 6 }}>
            Създай нова услуга и започни да получаваш поръчки от клиенти
          </Typography>
        </Box>

        {/* Success Message */}
        {success && (
          <Fade in={success}>
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              onClose={() => setSuccess(false)}
            >
              Услугата е създадена успешно! Пренасочваме към страницата с услуги...
            </Alert>
          </Fade>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Main Form */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            borderRadius: 4,
            backdropFilter: 'blur(10px)',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={4}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <DescriptionIcon sx={{ color: '#28A0F0', mr: 1 }} />
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    Основна информация
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Service Title"
                  placeholder="For example: Create a website with React"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  helperText="Minimum 10 characters"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                    '& .MuiFormHelperText-root': { color: '#a0a0a0' },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Service Description"
                  placeholder="Describe in detail what the service includes, what are your expertise, what the client gets..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  helperText="Minimum 50 characters. Be as detailed as possible for better results."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                    '& .MuiFormHelperText-root': { color: '#a0a0a0' },
                  }}
                />
              </Grid>

              {/* Pricing and Delivery */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                  <MoneyIcon sx={{ color: '#28A0F0', mr: 1 }} />
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    Ценообразуване и доставка
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Price (€)"
                  type="number"
                  placeholder="0"
                  value={formData.price || ''}
                  onChange={(e) => handleInputChange('price', e.target.value ? Number(e.target.value) : null)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#a0a0a0' }}>Тип цена</InputLabel>
                  <Select
                    value={formData.price_type}
                    onChange={(e) => handleInputChange('price_type', e.target.value)}
                    sx={{
                      color: '#ffffff',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                    }}
                  >
                    <MenuItem value="FIXED">Фиксирана цена</MenuItem>
                    <MenuItem value="HOURLY">На час</MenuItem>
                    <MenuItem value="DAILY">На ден</MenuItem>
                    <MenuItem value="WEEKLY">На седмица</MenuItem>
                    <MenuItem value="MONTHLY">На месец</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Delivery Time (days)"
                  type="number"
                  placeholder="0"
                  value={formData.delivery_days || ''}
                  onChange={(e) => handleInputChange('delivery_days', e.target.value ? Number(e.target.value) : null)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Number of Revisions"
                  type="number"
                  placeholder="1"
                  value={formData.revisions}
                  onChange={(e) => handleInputChange('revisions', Number(e.target.value))}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      color: '#ffffff',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: '#28A0F0' },
                      '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                    },
                    '& .MuiInputLabel-root': { color: '#a0a0a0' },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#a0a0a0' }}>Категория</InputLabel>
                  <Select
                    value={formData.category_id}
                    onChange={(e) => handleInputChange('category_id', e.target.value)}
                    sx={{
                      color: '#ffffff',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#28A0F0' },
                    }}
                  >
                    <MenuItem value="">Избери категория</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Skills */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                  <CodeIcon sx={{ color: '#28A0F0', mr: 1 }} />
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    Необходими умения
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={skillOptions}
                  value={formData.skills}
                  onChange={(_, newValue) => handleSkillsChange(newValue)}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        sx={{
                          backgroundColor: 'rgba(40, 160, 240, 0.2)',
                          color: '#28A0F0',
                          border: '1px solid rgba(40, 160, 240, 0.3)',
                          '& .MuiChip-deleteIcon': {
                            color: '#28A0F0',
                          },
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add Skills"
                      placeholder="Start typing or select from the list"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          color: '#ffffff',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&:hover fieldset': { borderColor: '#28A0F0' },
                          '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                        },
                        '& .MuiInputLabel-root': { color: '#a0a0a0' },
                        '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Features */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                  <StarIcon sx={{ color: '#28A0F0', mr: 1 }} />
                  <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 600 }}>
                    Функции и включвания
                  </Typography>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddFeature}
                  sx={{
                    borderColor: 'rgba(40, 160, 240, 0.5)',
                    color: '#28A0F0',
                    mb: 2,
                    '&:hover': {
                      borderColor: '#28A0F0',
                      backgroundColor: 'rgba(40, 160, 240, 0.1)',
                    },
                  }}
                >
                  Добави функция
                </Button>

                {formData.features.map((feature) => (
                  <Box key={feature.id} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      label="Feature Name"
                      value={feature.name}
                      onChange={(e) => handleUpdateFeature(feature.id, 'name', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          color: '#ffffff',
                          '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                          '&:hover fieldset': { borderColor: '#28A0F0' },
                          '&.Mui-focused fieldset': { borderColor: '#28A0F0' },
                        },
                        '& .MuiInputLabel-root': { color: '#a0a0a0' },
                        '& .MuiInputLabel-root.Mui-focused': { color: '#28A0F0' },
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={feature.included}
                          onChange={(e) => handleUpdateFeature(feature.id, 'included', e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#28A0F0',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#28A0F0',
                            },
                          }}
                        />
                      }
                      label="Included"
                      sx={{ color: '#ffffff', minWidth: '120px' }}
                    />
                    <IconButton
                      onClick={() => handleRemoveFeature(feature.id)}
                      sx={{ color: '#ef4444' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>

              {/* Action Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 4 }}>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    disabled={saving}
                    sx={{
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                      color: '#ffffff',
                      '&:hover': {
                        borderColor: '#28A0F0',
                        backgroundColor: 'rgba(40, 160, 240, 0.1)',
                      },
                    }}
                  >
                    Изчисти
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #28A0F0 0%, #1d4ed8 100%)',
                      color: '#ffffff',
                      px: 4,
                      py: 1.5,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                      },
                      '&:disabled': {
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {saving ? 'Публикуване...' : 'Публикувай услуга'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      <Footer />
    </Box>
  );
};

export default NewServicePage;
